import React, { useEffect, useState, useRef, useCallback } from "react";
import { Form, Row, Col, Button, Upload, message, Space, Progress, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { PlusCircleTwoTone, UploadOutlined, LoadingOutlined, CloseCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

import "../../../public/sass/components/drawer/_positionsContainer.scss";
import { EmptyDataCreate } from "../../EmptyDataCreate";
import { LoadingSpinner } from "../../LoadingSpinner";
import Api from "../../../api";
import ApiConstants from "../../../api/ApiConstants";
import PositionsRequestTable from "../../Tables/Positions-Request";
import { Filters } from "../../../components/Drawer/Positions-Action/Filter";

import { CreatePositions } from "./CreatePosition";
import { EditPositionProcedure } from "../../../components/Drawer/Positions/EditPositionProcedure";
import { toastNotification } from "../../../utils/toastNotification";
import { ExportToExcel } from "../../ExportToExcel";

export const PositionsContainer = ({ onClose, id, data, getCompanyById, setStatus }) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);

  const [editData, setData] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [third_parties, setThirdPartiesCompanies] = useState([]);
  const [isVisibleCreate, setVisibleCreate] = useState(false);
  const [modalState, setModal] = useState({ id: "", mode: "", isOpen: false });
  const [importPhase, setImportPhase] = useState("idle");
  const [importProgress, setImportProgress] = useState(null);
  const [importJobId, setImportJobId] = useState(null);
  const pollRef = useRef(null);

  const handleCancel = useCallback(async () => {
    if (!importJobId) return;
    try {
      await Api("import/jobs/" + importJobId + "/cancel", "POST");
    } catch (e) {
      console.error("[Import] Cancel error:", e);
    }
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    localStorage.removeItem("activeImportJobId_request_" + data?.id);
    setImportPhase("idle");
    setImportProgress(null);
    setImportJobId(null);
    getProductsByRequestId();
    toastNotification("info", t("import_cancelled"));
  }, [importJobId, data?.id, t]);

  const startPolling = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const statusRes = await Api("import/jobs/" + jobId, "GET");
        setImportProgress({
          total: statusRes.total_rows,
          current: statusRes.done_rows + statusRes.failed_rows,
          succeeded: statusRes.done_rows,
          failed: statusRes.failed_rows,
          errors: statusRes.errors || [],
        });
        getProductsByRequestId();
        if (statusRes.status === "completed" || statusRes.status === "failed" || statusRes.status === "cancelled") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          localStorage.removeItem("activeImportJobId_request_" + data?.id);
          setImportPhase("idle");
          setImportProgress(null);
          setImportJobId(null);
          getProductsByRequestId();
          if (statusRes.status !== "cancelled") {
            toastNotification(
              "success",
              `${t("import_completed")} (${statusRes.done_rows} ${t("succeeded")}, ${statusRes.failed_rows} ${t("failed")})`
            );
          }
        }
      } catch (pollErr) {
        console.error("[Import] Poll error:", pollErr);
      }
    }, 2000);
  }, [data?.id, t]);

  useEffect(() => {
    const stored = localStorage.getItem("activeImportJobId_request_" + data?.id);
    if (stored) {
      Api("import/jobs/" + stored, "GET")
        .then((statusRes) => {
          if (statusRes.status === "pending" || statusRes.status === "processing") {
            setImportJobId(stored);
            setImportPhase("processing");
            setImportProgress({
              total: statusRes.total_rows,
              current: statusRes.done_rows + statusRes.failed_rows,
              succeeded: statusRes.done_rows,
              failed: statusRes.failed_rows,
              errors: statusRes.errors || [],
            });
            startPolling(stored);
          } else {
            localStorage.removeItem("activeImportJobId_request_" + data?.id);
          }
        })
        .catch(() => {
          localStorage.removeItem("activeImportJobId_request_" + data?.id);
        });
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [data?.id, startPolling]);

  const toggleDrawer = () => setVisibleCreate(!isVisibleCreate);

  const handleSubmit = () => {
    onClose();
  };

  const handleModal = (mode, isOpen, id) => {
    setModal({
      ...modalState,
      ["mode"]: mode,
      ["isOpen"]: isOpen,
      ["id"]: id,
    });
  };

  useEffect(() => {
    getAllCompanies();
    handleThirdPartiesCompanies();
    handleGetAllCategories();
    setLoadingPositions(false);
    setProducts(data?.products);
  }, []);

  useEffect(() => {
    setProducts(data?.products || []);
  }, [data?.products]);

    setStatus(false);
    products.map((item) => {
      if (item.status !== 0) {
        setStatus(true);
      }
    });

  const handleGetAllCategories = () => {
    Api("categories", "GET", null)
      .then((data) => {
        setCategories(data.results);
      })
      .catch(() => setLoadingCategories(false));
  };

  const getAllCompanies = () => {
    Api("actions", "GET").then((data) => {
      setCompanies(data.results);
    });
  };

  const handleThirdPartiesCompanies = () => {
    Api("third-party", "GET", null).then((data) => {
      setThirdPartiesCompanies(data.companies);
    });
  };

  const getProductById = (id) => {
    setLoading(true);
    Api("products/" + id, "GET", null)
      .then((data) => {
        setData(data);
        handleModal("edit", true, id);
        setLoading(false);
      })
      .catch((err) => {
        toastNotification("error", err.error || `${t("something_went_wrong")}`);
        setLoading(false);
      });
  };

  const deleteProduct = (id) => {
    if (!id) return message.error("Id is missing!");
    Api("products/" + id, "DELETE", null)
      .then(() => {
        getProductsByRequestId();
        toastNotification("success", `${t("product_successfully_deleted")}`);
      })
      .catch(() => toastNotification("error", `${t("error_delete_product")}`));
  };

  const getProductsByRequestId = () => {
    Api("products/getProductsByRequestId/" + data?.id, "GET", null)
      .then((results) => {
        setProducts(results);
      })
      .catch(() => setLoading(false));
  };

  const handleUploadExcel = async (file) => {
    const company_id = localStorage.getItem("company_id").replace(/"/g, "");
    setImportPhase("uploading");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", company_id);
    formData.append("request_id", data?.id);

    try {
      const uploadRes = await fetch(ApiConstants.BASE_URL + "import/upload", {
        method: "POST",
        headers: {
          Authorization: "Bearer " + JSON.parse(localStorage.getItem("auc-token")),
        },
        body: formData,
      });
      const result = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(result.message || "Upload failed");

      const jobId = result.import_job_id;
      localStorage.setItem("activeImportJobId_request_" + data?.id, jobId);
      setImportJobId(jobId);
      setImportPhase("processing");
      setImportProgress({ total: result.total_rows, current: 0, succeeded: 0, failed: 0, errors: [] });
      toastNotification("info", `${t("import_started")} (${result.total_rows} ${t("rows")})`);
      startPolling(jobId);
    } catch (err) {
      console.error("[Import] Fatal error:", err);
      setImportPhase("idle");
      setImportProgress(null);
      setImportJobId(null);
      toastNotification("error", `Import failed: ${err.message}`);
    }
  };

  const dataFormatExcel = [
    {
      Positionsname: "",
      Positionsnummer: "",
      Beschreibung: "",
      Kurzbeschreibung: "",
      Verpackungseinheit: "",
      "Menge je VE": "",
      Laufleistung: "",
      "Typ der Laufleistung": "",
      "Art des Gutachtens": "",
      "Zerschlagungswert menge": "",
      Zwischensumme: "",
      "MwSt. - Art": "",
      "MwSt.": "",
      "Gesamtwert inkl. MwSt.": "",
      "Fortführungswert menge": "",
      Zwischensumme: "",
      "MwSt. - Art	MwSt.": "",
      "Gesamtwert inkl. MwSt": "",
      "Marktwert menge": "",
      Zwischensumme: "",
      "MwSt. - Art": "",
      MwSt: "",
      "Gesamtwert inkl. MwSt.": "",
      Vermögensart: "",
      "Aus- und Absonderungsrechte": "",
      Aussonderungsrechte: "",
      Absonderungsrechte: "",
      "Platz des Artikels": "",
    },
  ];

  if (loadingPositions) return <LoadingSpinner />;
  if (!products?.length)
    return (
      <>
        <EmptyDataCreate
          btnTitle={t("add_position")}
          message={t("no_positions_found")}
          handleOnClick={toggleDrawer}
        />
        <CreatePositions
          id={id}
          isVisible={isVisibleCreate}
          onClose={toggleDrawer}
          handleGetAllProducts={getProductsByRequestId}
          getCompanyById={getCompanyById}
          companies={companies}
          categories={categories}
          third_parties={third_parties}
          products={products}
          request_id={data?.id}
        />
      </>
    );

  return (
    <Form
      form={form}
      id="customers-form"
      layout="horizontal"
      scrollToFirstError
      onFinish={handleSubmit}
      className="form-style"
    >
      <Row gutter={16}>
        {data.status !== 3 && (
          <>
            <Col span={10}>
              <Form.Item name="more_products">
                <Button className="button-style" onClick={toggleDrawer}>
                  <PlusCircleTwoTone className="icon-style" />
                  {t("more_products")}
                </Button>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="files">
                {importPhase === "idle" && (
                  <Upload
                    accept=".xlsm, .xltm, .xlt, .xls, .xlsx, .xltx, .xlsb, .xml, .xlam, .xla, .xlw, .xlr"
                    beforeUpload={(file) => {
                      handleUploadExcel(file);
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />}>{t("upload_excel")}</Button>
                  </Upload>
                )}
                {importPhase === "uploading" && (
                  <div className="import-progress-inline">
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                    <span>{t("uploading")}...</span>
                  </div>
                )}
                {importPhase === "processing" && (
                  <div className="import-progress-inline">
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                    <span>
                      {importProgress?.current || 0}/{importProgress?.total || 0} {t("positions")}
                    </span>
                    <Progress
                      percent={importProgress?.total ? Math.round((importProgress.current / importProgress.total) * 100) : 0}
                      size="small"
                      style={{ width: 80, margin: 0 }}
                    />
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<CloseCircleOutlined />}
                      onClick={handleCancel}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>

            <Col span={6} className="push-right">
              <ExportToExcel
                t={t}
                apiData={dataFormatExcel}
                fileName="PositionenImport"
              />
            </Col>
          </>
        )}

        <Filters />
        <PositionsRequestTable
          products={products}
          getProductById={getProductById}
          deleteProduct={deleteProduct}
          data={data}
          // loading={loading}
        />
        {/* {data?.products?.map((item, idx) => {
          const imgParse = JSON.parse(item?.images);
          return (
            <Row key={idx} className="row-style">
              <Col style={{ width: 200 }}>
                <Image
                  src={ApiConstants.IMAGE_URL + imgParse[0]}
                  width={100}
                  fallback="data:image/png;base64,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"
                />
              </Col>
              <Col style={{ width: 200 }} className="column-style">
                <Input disabled value={item?.name} />
                <Input disabled value={item?.rights} />
              </Col>
              <Col style={{ width: 200 }}>
                <Input disabled value={item?.breakdown_value} />
                <Input disabled value="Rudolstaedter Strasse 20" />
              </Col>
            </Row>
          );
        })} */}
      </Row>
      <EditPositionProcedure
        editData={editData}
        id={modalState.id}
        isVisible={modalState.mode == "edit" && modalState.isOpen}
        handleGetAllProducts={getProductsByRequestId}
        onClose={handleModal}
        companies={companies}
        third_parties={third_parties}
        categories={categories}
        products={products}
      />
      <CreatePositions
        id={id}
        isVisible={isVisibleCreate}
        onClose={toggleDrawer}
        handleGetAllProducts={getProductsByRequestId}
        getCompanyById={getCompanyById}
        companies={companies}
        categories={categories}
        third_parties={third_parties}
        products={products}
        request_id={data?.id}
      />
    </Form>
  );
};
